import { Component, HostListener, Input, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { ThemeManagerService } from '~/services/theme-manager';
import { MenuService, MenuItem } from '~/services/administration.ts/menu';

@Component({
    selector: 'admin-section',
    templateUrl: './admin-section.html',
    styleUrls: ['./admin-section.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class AdminSectionComponent implements OnInit {
  @Input() base: string;

  public sections: any = {
    AccountInformation: {
      isExpandedHeaders: true,
    },
    Alerts: {
      isExpandedHeaders: true,
    },
    BarcodeSettings: {
      isExpandedHeaders: true,
    },
    GeneralConfiguration: {
      isExpandedHeaders: true,
    },
    Inventory: {
      isExpandedHeaders: true,
    },
    OtherActions: {
      isExpandedHeaders: true,
    },
    Order: {
      isExpandedHeaders: true,
    },
    QuickbooksSettings: {
      isExpandedHeaders: true,
    },
    Transactions: {
      isExpandedHeaders: true,
    },
    Configure: {
      isExpandedHeaders: true,
    },
    Mobile: {
      isExpandedHeaders: true,
    },
    MobileTransactions: {
      isExpandedHeaders: true,
    },
    QuickbooksSection: {
      isExpandedHeaders: true,
    },
    ActiveDirectory: {
      isExpandedHeaders: true,
    },
  };

  _theme: string;
  _searchValue: string = '';
  
  adminMenu: MenuItem[] = [];
  menuItemsMap = new Map<string, MenuItem>();

  constructor(
    private _menuService: MenuService,
    private _themeManager: ThemeManagerService,
    private router: Router
  ) {
    this._themeManager.themeChange.subscribe((data) => {
      this._theme = data;
    });
  }

  expandCollapseSections(sectionName: string) {
    this.sections[sectionName].isExpandedHeaders = !this.sections[sectionName].isExpandedHeaders;
  }
  doSearch() {
    this.adminMenu.forEach(section => {
      const key = section.label.replace(/\s+/g, '');
      if (this.sections[key]) {
        if (!this._searchValue) {
          this.sections[key].isExpandedHeaders = true;
        } else {
          const hasMatchingChild = section.children && section.children.some(child =>
            child.label.toLowerCase().trim().includes(this._searchValue.toLowerCase().trim())
          );
          this.sections[key].isExpandedHeaders = !!hasMatchingChild;
        }
      }
    });
  }


  _expandAll() {
    //this.sections.forEach((f:any) => {f.isExpandedHeaders = true});
    const sectionKeys = Object.keys(this.sections);
    sectionKeys.forEach((key) => {
      this.sections[key].isExpandedHeaders = true;
    });
  }

  _collapseAll() {
    //this.sections.forEach((f:any) => {f.isExpandedHeaders = false});
    const sectionKeys = Object.keys(this.sections);
    sectionKeys.forEach((key) => {
      this.sections[key].isExpandedHeaders = false;
    });
  }

  async LoadData() {
    this._menuService.getAdminMenu(this.base).subscribe((menu) => {
      this.adminMenu = menu || [];
      this.menuItemsMap.clear();
      this.buildMenuMap(this.adminMenu);
    });
  }

  private buildMenuMap(items: MenuItem[]) {
    for (const item of items) {
      if (item.route) {
        // Normalize route by removing leading slash and trimming
        const normalizedRoute = item.route.replace(/^\//, '').toLowerCase().trim();
        this.menuItemsMap.set(normalizedRoute, item);
      }
      if (item.children && item.children.length > 0) {
        this.buildMenuMap(item.children);
      }
    }
  }

  isItemVisible(route: string): boolean {
    const normalizedRoute = route.replace(/^\//, '').toLowerCase().trim();
    const item = this.menuItemsMap.get(normalizedRoute);
    return item ? item.visible : false;
  }

  getItemStatus(route: string): string {
    const normalizedRoute = route.replace(/^\//, '').toLowerCase().trim();
    const item = this.menuItemsMap.get(normalizedRoute);
    return item && item['status'] ? item['status'] : '';
  }

  private sectionRoutes: { [key: string]: { stock: string[], asset: string[] } } = {
    AccountInformation: {
      stock: ['stock/admin/license'],
      asset: []
    },
    Alerts: {
      stock: [
        'stock/admin/advanced-stock-expiration',
        'stock/admin/inventory-high',
        'stock/admin/inventory-low',
        'stock/admin/inventory-out'
      ],
      asset: [
        'asset/admin/schedule-settings',
        'asset/admin/check-out-due-date',
        'asset/admin/reservation-alert',
        'asset/admin/maintenance-contract-end-date',
        'asset/admin/warranty-End-Date-Alert',
        'asset/admin/schedule-maintenance-alert'
      ]
    },
    BarcodeSettings: {
      stock: [
        'stock/admin/barcode-generators',
        'stock/admin/admin-barcode-label-print',
        'stock/admin/UPC-online-lookup'
      ],
      asset: [
        'asset/admin/barcode-generators',
        'asset/admin/admin-barcode-label-print',
        'asset/admin/UPC-online-lookup'
      ]
    },
    QuickbooksSection: {
      stock: [
        'stock/admin/quickbooks-authentication',
        'stock/admin/quickbooks-settings'
      ],
      asset: []
    },
    ActiveDirectory: {
      stock: [
        'common/stock/active-directory-connection',
        'common/stock/active-directory-integration',
        'common/stock/active-directory-person-mapping',
        'common/stock/active-directory-secuirty-group-mapping',
        'common/stock/active-directory-site-mapping'
      ],
      asset: [
        'common/asset/active-directory-connection',
        'common/asset/active-directory-integration',
        'common/asset/active-directory-person-mapping',
        'common/asset/active-directory-secuirty-group-mapping',
        'common/asset/active-directory-site-mapping'
      ]
    },
    GeneralConfiguration: {
      stock: [
        'stock/admin/saml-configurations',
        'stock/admin/cost-price-format',
        'stock/admin/date-format',
        'stock/admin/drop-down-settings',
        'stock/admin/graph-settings',
        'stock/admin/hyperlink-icon',
        'stock/admin/image-settings',
        'stock/admin/reset-fields-names'
      ],
      asset: [
        'asset/admin/saml-configurations',
        'asset/admin/cost-price-format',
        'asset/admin/date-format',
        'asset/admin/drop-down-settings',
        'asset/admin/graph-settings',
        'asset/admin/hyperlink-icon',
        'asset/admin/image-settings',
        'asset/admin/reset-fields-names',
        'asset/admin/asset-model-settings',
        'asset/admin/funding-source-settings'
      ]
    },
    Inventory: {
      stock: [
        'stock/admin/historical-inventory',
        'stock/admin/0-quantity-settings',
        'stock/admin/stock-quantity-format',
        'stock/admin/stock-cycle-count-settings'
      ],
      asset: []
    },
    OtherActions: {
      stock: [
        'stock/import',
        'stock/admin/logged-users',
        'stock/admin/security-levels'
      ],
      asset: [
        'asset/import',
        'asset/admin/logged-users',
        'asset/admin/security-levels'
      ]
    },
    Order: {
      stock: [
        'stock/admin/po-approvals',
        'stock/admin/po-preferences',
        'stock/admin/so-approvals',
        'stock/admin/so-preferences',
        'stock/admin/so-to-po-preferences'
      ],
      asset: [
        'asset/admin/asset-po-approvals',
        'asset/admin/po-preferences'
      ]
    },
    Transactions: {
      stock: [
        'stock/admin/assemble-settings',
        'stock/admin/issue-settings',
        'stock/admin/move-settings',
        'stock/admin/pack-settings',
        'stock/admin/picklist-settings',
        'stock/admin/history-settings',
        'stock/admin/print-receipts',
        'stock/admin/receive-settings',
        'stock/admin/return-settings',
        'stock/admin/ship-settings',
        'stock/admin/transaction-date-editing',
        'stock/admin/stock-authorization-settings',
        'stock/admin/checkout-in-settings'
      ],
      asset: [
        'asset/admin/asset-cyclecount-settings',
        'asset/admin/asset-print-receipts',
        'asset/admin/picklist-settings',
        'asset/admin/asset-receive-settings',
        'asset/admin/transaction-date-editing',
        'asset/admin/history-settings',
        'asset/admin/check-out-in-settings',
        'asset/admin/asset-dispose-settings',
        'asset/admin/asset-adjust-settings',
        'asset/admin/asset-physical-inventory-settings',
        'asset/admin/asset-authorization-settings'
      ]
    },
    Mobile: {
      stock: [
        'stock/admin/mobile-setting',
        'stock/admin/mobile-device-managment'
      ],
      asset: [
        'asset/admin/asset-mobile-setting',
        'asset/admin/mobile-device-managment'
      ]
    },
    MobileTransactions: {
      stock: [
        'stock/admin/pack-settings-mobile'
      ],
      asset: []
    }
  };

  isSectionVisible(sectionName: string): boolean {
    if (sectionName === 'OtherActions') return true;

    const routesObj = this.sectionRoutes[sectionName];
    if (!routesObj) return false;

    const routes = this.base === 'stock' ? routesObj.stock : routesObj.asset;
    return routes.some(route => this.isItemVisible(route));
  }




  NavigateToPurchaseOrderPreference() {
    if (this.base.toLowerCase() == 'asset'.toLocaleLowerCase()) {
      this.router.navigate(['asset/admin/po-preferences']);
    }
    else {
      this.router.navigate(['stock/admin/po-preferences']);
    }
  }

  NavigateToBarcodeGenerator() {
    if (this.base.toLowerCase() == 'asset'.toLocaleLowerCase()) {
      this.router.navigate(['asset/admin/barcode-generators']);
    }
    else {
      this.router.navigate(['stock/admin/barcode-generators']);
    }
  }

  NavigateToPrintLabelPreference() {
    if (this.base.toLowerCase() == 'asset'.toLocaleLowerCase()) {
      this.router.navigate(['asset/admin/admin-barcode-label-print']);
    }
    else {
      this.router.navigate(['stock/admin/admin-barcode-label-print']);
    }
  }

  NavigateUPCOnline() {
    if (this.base.toLowerCase() == 'asset'.toLocaleLowerCase()) {
      this.router.navigate(['asset/admin/UPC-online-lookup']);
    }
    else {
      this.router.navigate(['stock/admin/UPC-online-lookup']);
    }
  }

  NavigateToReceiveSettings() {
    if (this.base.toLowerCase() == 'asset'.toLocaleLowerCase()) {
      this.router.navigate(['asset/admin/asset-receive-settings']);
    }
    else {
      this.router.navigate(['stock/admin/receive-settings']);
    }
  }

  @HostListener('window:resize')

  ngOnInit(): void {
    let windowSize = window.innerWidth;

    if (windowSize <= 991) {
      for (const key of Object.keys(this.sections)) {
        this.sections[key].isExpandedHeaders = false;
      }
    }
    else {
      for (const key of Object.keys(this.sections)) {
        this.sections[key].isExpandedHeaders = true;
      }
    }

    this.LoadData();
  }

  _navigate(url: string) {
    this.router.navigate([url]);
  }
}
